import { useEffect, useState } from 'react';
import { api, BladApi, type Sesja, type ZdarzenieAudytu } from '../api';
import { dataICzas } from '../format';
import { NaglowekWidoku } from '../components/Shell';
import { Dana, Karta, Komunikat, Pusto } from '../components/ui';

/**
 * Konto — kim jesteś, do czego masz prawo i co system o tym zapisał.
 *
 * Widok z Etapu 1; po dojściu kart urządzeń przestał być stroną główną i wrócił
 * na swoje miejsce: zakładkę dla administratora i dla każdego, kto chce sprawdzić,
 * dlaczego czegoś nie widzi.
 */
export function PanelView({ sesja }: { sesja: Sesja }) {
  const { user, permissions } = sesja;
  const mozeCzytacDziennik = permissions.includes('audit.read');

  const [zdarzenia, setZdarzenia] = useState<ZdarzenieAudytu[] | null>(null);
  const [bladDziennika, setBladDziennika] = useState<string | null>(null);

  useEffect(() => {
    if (!mozeCzytacDziennik) return;
    let aktualne = true;

    api
      .dziennik(20)
      .then((wynik) => aktualne && setZdarzenia(wynik.zdarzenia))
      .catch((error: unknown) => {
        if (aktualne) {
          setBladDziennika(error instanceof BladApi ? error.message : 'Nie udało się wczytać.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [mozeCzytacDziennik]);

  const grupy = [...new Set(permissions.map((kod) => kod.split('.')[0]!))].sort();

  return (
    <>
      <NaglowekWidoku tytul="Konto" podtytul={`${user.imieNazwisko} · ${user.rolaNazwa}`} />

      <div className="grid gap-14 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          <Karta tytul="Dziennik zdarzeń" wypelnienie={false}>
            {!mozeCzytacDziennik && (
              <p className="t-small px-14 py-14 text-n-600">
                Twoja rola nie ma uprawnienia <span className="t-mono-data">audit.read</span>.
              </p>
            )}

            {mozeCzytacDziennik && bladDziennika && (
              <div className="p-14">
                <Komunikat tresc={bladDziennika} />
              </div>
            )}

            {mozeCzytacDziennik && !bladDziennika && zdarzenia === null && (
              <Pusto tresc="Wczytywanie…" />
            )}

            {mozeCzytacDziennik && zdarzenia?.length === 0 && <Pusto tresc="Brak zdarzeń." />}

            {mozeCzytacDziennik && zdarzenia && zdarzenia.length > 0 && (
              <div className="overflow-x-auto">
                <table className="tab">
                  <thead>
                    <tr>
                      <th>Kiedy</th>
                      <th>Akcja</th>
                      <th>Encja</th>
                      <th>Kto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zdarzenia.map((zdarzenie) => (
                      <tr key={zdarzenie.id}>
                        <td className="t-mono-data whitespace-nowrap text-n-600">
                          {dataICzas(zdarzenie.at)}
                        </td>
                        <td className="t-mono-data text-n-900">{zdarzenie.akcja}</td>
                        <td className="t-mono-data text-n-600">{zdarzenie.encja}</td>
                        <td className="t-small text-n-700">
                          {zdarzenie.user?.imieNazwisko ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Karta>
        </div>

        <div className="min-w-0">
          <Karta tytul="Konto">
            <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
              <Dana etykieta="Imię i nazwisko">{user.imieNazwisko}</Dana>
              <Dana etykieta="E-mail" mono>
                {user.email}
              </Dana>
              <Dana etykieta="Rola">{user.rolaNazwa}</Dana>
              <Dana etykieta="Firma">{user.tenant.nazwa}</Dana>
              <Dana etykieta="Identyfikator firmy" mono>
                {user.tenant.slug}
              </Dana>
            </dl>
          </Karta>

          <Karta tytul={`Uprawnienia · ${permissions.length}`}>
            {grupy.map((grupa) => (
              <div key={grupa} className="mb-13 last:mb-0">
                <div className="t-mono-micro mb-7 text-n-500">{grupa}</div>
                <div className="flex flex-wrap gap-5">
                  {permissions
                    .filter((kod) => kod.startsWith(`${grupa}.`))
                    .map((kod) => (
                      <span
                        key={kod}
                        className="t-mono-data border border-n-100 bg-n-50 px-7 py-4 text-n-700"
                      >
                        {kod}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </Karta>
        </div>
      </div>
    </>
  );
}
